import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Car, UserCog } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Iter 156 — Catálogos: Vehículos de alquiler + Personas de contacto.
// Roles con permiso de escritura: super_admin, admin, jefe_taller.
// Recepción puede leer pero no escribir.
export default function RentalCatalogsPage() {
  const { token, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(() => searchParams.get("tab") === "contacts" ? "contacts" : "vehicles");

  // Iter 157 — sincronizar tab activo con URL para enlaces directos desde el sidebar
  useEffect(() => {
    const urlTab = searchParams.get("tab") === "contacts" ? "contacts" : "vehicles";
    if (urlTab !== tab) setTab(urlTab);
  }, [searchParams]);  // eslint-disable-line react-hooks/exhaustive-deps

  const changeTab = (next) => {
    setTab(next);
    setSearchParams(next === "contacts" ? { tab: "contacts" } : {}, { replace: true });
  };
  const [vehicles, setVehicles] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {type:"vehicle"|"contact", data:{...}}
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [confirmReactivate, setConfirmReactivate] = useState(null);
  // Iter 161 — filtro por estado: 'active' | 'inactive' | 'all'
  const [statusFilter, setStatusFilter] = useState("active");

  const canEdit = ["super_admin", "admin", "jefe_taller"].includes(user?.role || "");

  const axiosAuth = () => axios.create({
    baseURL: API,
    headers: { Authorization: `Bearer ${token}` },
  });

  const reload = async () => {
    setLoading(true);
    try {
      const [v, c, ce] = await Promise.all([
        axiosAuth().get("/rental-catalog/vehicles?include_inactive=true"),
        axiosAuth().get("/rental-catalog/contacts?include_inactive=true"),
        axiosAuth().get("/centers?active_only=true"),
      ]);
      setVehicles(Array.isArray(v.data) ? v.data : []);
      setContacts(Array.isArray(c.data) ? c.data : []);
      setCenters(Array.isArray(ce.data) ? ce.data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando catálogos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const newVehicle = () => setEditing({
    type: "vehicle",
    data: { active: true, display_name: "", plate: "", brand: "", model: "", category: "", centers: [], internal_notes: "", order: 0 },
  });
  const newContact = () => setEditing({
    type: "contact",
    data: { active: true, display_name: "", phone: "", email: "", center: "", order: 0 },
  });

  const save = async () => {
    if (!editing) return;
    const { type, data } = editing;
    if (!(data.display_name || "").trim()) {
      toast.error("El nombre visible es obligatorio");
      return;
    }
    const endpoint = type === "vehicle" ? "/rental-catalog/vehicles" : "/rental-catalog/contacts";
    try {
      if (data.id) {
        await axiosAuth().put(`${endpoint}/${data.id}`, data);
        toast.success("Guardado");
      } else {
        await axiosAuth().post(endpoint, data);
        toast.success("Creado");
      }
      setEditing(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando");
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    if (deleteConfirmText !== "DESACTIVAR") {
      toast.error('Escribe "DESACTIVAR" (en mayúsculas) para confirmar');
      return;
    }
    const { type, id } = confirmDelete;
    const endpoint = type === "vehicle" ? "/rental-catalog/vehicles" : "/rental-catalog/contacts";
    try {
      await axiosAuth().delete(`${endpoint}/${id}`);
      toast.success("Desactivado");
      setConfirmDelete(null);
      setDeleteConfirmText("");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error desactivando");
    }
  };

  const doReactivate = async () => {
    if (!confirmReactivate) return;
    const { type, id } = confirmReactivate;
    const endpoint = type === "vehicle" ? "/rental-catalog/vehicles" : "/rental-catalog/contacts";
    try {
      await axiosAuth().post(`${endpoint}/${id}/reactivate`);
      toast.success("Reactivado");
      setConfirmReactivate(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error reactivando");
    }
  };

  // Iter 161 — filtrado por estado
  const applyFilter = (items) => {
    if (statusFilter === "active") return items.filter(x => x.active !== false);
    if (statusFilter === "inactive") return items.filter(x => x.active === false);
    return items;
  };
  const filteredVehicles = applyFilter(vehicles);
  const filteredContacts = applyFilter(contacts);
  const activeVehiclesCount = vehicles.filter(v => v.active !== false).length;
  const inactiveVehiclesCount = vehicles.filter(v => v.active === false).length;
  const activeContactsCount = contacts.filter(c => c.active !== false).length;
  const inactiveContactsCount = contacts.filter(c => c.active === false).length;

  const editForm = editing?.data || {};
  const setField = (k, v) => setEditing(e => ({ ...e, data: { ...e.data, [k]: v } }));

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="rental-catalogs-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Vehículos de alquiler</h1>
          <p className="text-sm text-slate-500 mt-1">
            Catálogo editable de la flota de alquiler y de las personas de contacto.
            {!canEdit && <span className="ml-2 text-amber-600">(Solo lectura para tu rol)</span>}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 mb-4">
        <button
          data-testid="tab-vehicles"
          onClick={() => changeTab("vehicles")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === "vehicles" ? "border-slate-800 text-slate-800" : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <Car className="inline w-4 h-4 mr-1" /> Vehículos ({activeVehiclesCount}{inactiveVehiclesCount > 0 ? ` · ${inactiveVehiclesCount} desact.` : ""})
        </button>
        <button
          data-testid="tab-contacts"
          onClick={() => changeTab("contacts")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === "contacts" ? "border-slate-800 text-slate-800" : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <UserCog className="inline w-4 h-4 mr-1" /> Personas de contacto ({activeContactsCount}{inactiveContactsCount > 0 ? ` · ${inactiveContactsCount} desact.` : ""})
        </button>
      </div>

      {/* Iter 161 — Filtro por estado */}
      <div className="flex items-center gap-1 mb-4" data-testid="status-filter-bar">
        {[
          { value: "active", label: `Activos (${tab === "vehicles" ? activeVehiclesCount : activeContactsCount})` },
          { value: "inactive", label: `Desactivados (${tab === "vehicles" ? inactiveVehiclesCount : inactiveContactsCount})` },
          { value: "all", label: "Todos" },
        ].map(opt => (
          <button
            key={opt.value}
            data-testid={`filter-${opt.value}`}
            onClick={() => setStatusFilter(opt.value)}
            className={`px-3 py-1 text-xs rounded-sm border transition-colors ${
              statusFilter === opt.value
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-slate-400">Cargando…</p>
      ) : tab === "vehicles" ? (
        <Card className="p-4">
          {canEdit && (
            <div className="flex justify-end mb-3">
              <Button onClick={newVehicle} data-testid="btn-new-vehicle" size="sm">
                <Plus className="w-4 h-4 mr-1" /> Nuevo vehículo
              </Button>
            </div>
          )}
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-slate-500 border-b">
              <tr>
                <th className="text-left py-2">Activo</th>
                <th className="text-left py-2">Nombre</th>
                <th className="text-left py-2">Matrícula</th>
                <th className="text-left py-2">Marca / Modelo</th>
                <th className="text-left py-2">Categoría</th>
                <th className="text-left py-2">Centros</th>
                <th className="text-right py-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredVehicles.length === 0 && (
                <tr><td colSpan="7" className="text-center py-8 text-slate-400">
                  {statusFilter === "inactive" ? "Sin vehículos desactivados" : "Sin vehículos en el catálogo"}
                </td></tr>
              )}
              {filteredVehicles.map(v => (
                <tr key={v.id} className={`border-b border-slate-100 hover:bg-slate-50 ${v.active === false ? "opacity-60" : ""}`} data-testid={`vehicle-row-${v.id}`}>
                  <td className="py-2">
                    {v.active === false ? (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] uppercase tracking-wider font-bold bg-slate-200 text-slate-600">Desactivado</span>
                    ) : "✓"}
                  </td>
                  <td className="py-2 font-semibold">{v.display_name}</td>
                  <td className="py-2 font-mono">{v.plate || "—"}</td>
                  <td className="py-2">{[v.brand, v.model].filter(Boolean).join(" ") || "—"}</td>
                  <td className="py-2">{v.category || "—"}</td>
                  <td className="py-2">{(v.centers || []).join(", ") || "todos"}</td>
                  <td className="py-2 text-right">
                    {canEdit && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => setEditing({ type: "vehicle", data: { ...v } })}
                          data-testid={`edit-vehicle-${v.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        {v.active === false ? (
                          <Button variant="ghost" size="sm" onClick={() => setConfirmReactivate({ type: "vehicle", id: v.id })}
                            data-testid={`reactivate-vehicle-${v.id}`}
                            className="text-xs text-emerald-700 hover:bg-emerald-50">
                            Reactivar
                          </Button>
                        ) : (
                          <Button variant="ghost" size="sm" onClick={() => { setConfirmDelete({ type: "vehicle", id: v.id }); setDeleteConfirmText(""); }}
                            data-testid={`delete-vehicle-${v.id}`}
                            className="text-xs text-slate-600 hover:bg-slate-100">
                            Desactivar
                          </Button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ) : (
        <Card className="p-4">
          {canEdit && (
            <div className="flex justify-end mb-3">
              <Button onClick={newContact} data-testid="btn-new-contact" size="sm">
                <Plus className="w-4 h-4 mr-1" /> Nueva persona
              </Button>
            </div>
          )}
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-slate-500 border-b">
              <tr>
                <th className="text-left py-2">Activo</th>
                <th className="text-left py-2">Nombre</th>
                <th className="text-left py-2">Teléfono</th>
                <th className="text-left py-2">Email</th>
                <th className="text-left py-2">Centro</th>
                <th className="text-right py-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredContacts.length === 0 && (
                <tr><td colSpan="6" className="text-center py-8 text-slate-400">
                  {statusFilter === "inactive" ? "Sin personas desactivadas" : "Sin personas en el catálogo"}
                </td></tr>
              )}
              {filteredContacts.map(c => (
                <tr key={c.id} className={`border-b border-slate-100 hover:bg-slate-50 ${c.active === false ? "opacity-60" : ""}`} data-testid={`contact-row-${c.id}`}>
                  <td className="py-2">
                    {c.active === false ? (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] uppercase tracking-wider font-bold bg-slate-200 text-slate-600">Desactivado</span>
                    ) : "✓"}
                  </td>
                  <td className="py-2 font-semibold">{c.display_name}</td>
                  <td className="py-2 font-mono">{c.phone || "—"}</td>
                  <td className="py-2 text-xs">{c.email || "—"}</td>
                  <td className="py-2">{c.center || "todos"}</td>
                  <td className="py-2 text-right">
                    {canEdit && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => setEditing({ type: "contact", data: { ...c } })}
                          data-testid={`edit-contact-${c.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        {c.active === false ? (
                          <Button variant="ghost" size="sm" onClick={() => setConfirmReactivate({ type: "contact", id: c.id })}
                            data-testid={`reactivate-contact-${c.id}`}
                            className="text-xs text-emerald-700 hover:bg-emerald-50">
                            Reactivar
                          </Button>
                        ) : (
                          <Button variant="ghost" size="sm" onClick={() => { setConfirmDelete({ type: "contact", id: c.id }); setDeleteConfirmText(""); }}
                            data-testid={`delete-contact-${c.id}`}
                            className="text-xs text-slate-600 hover:bg-slate-100">
                            Desactivar
                          </Button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Editor */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="rental-catalog-editor">
          <DialogHeader>
            <DialogTitle>
              {editing?.type === "vehicle"
                ? (editForm.id ? "Editar vehículo" : "Nuevo vehículo de alquiler")
                : (editForm.id ? "Editar persona" : "Nueva persona de contacto")}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-3">
                <Switch checked={!!editForm.active} onCheckedChange={(v) => setField("active", v)}
                  data-testid="editor-active" />
                <Label className="cursor-pointer" onClick={() => setField("active", !editForm.active)}>
                  Activo
                </Label>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Nombre visible *</Label>
                <Input value={editForm.display_name || ""} data-testid="editor-display-name"
                  onChange={(e) => setField("display_name", e.target.value)} />
              </div>

              {editing.type === "vehicle" && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Matrícula</Label>
                      <Input value={editForm.plate || ""} onChange={(e) => setField("plate", e.target.value.toUpperCase())}
                        data-testid="editor-plate" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Categoría</Label>
                      <Input value={editForm.category || ""} onChange={(e) => setField("category", e.target.value)}
                        placeholder="pequeño, compacto, SUV…" data-testid="editor-category" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Marca</Label>
                      <Input value={editForm.brand || ""} onChange={(e) => setField("brand", e.target.value)} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Modelo</Label>
                      <Input value={editForm.model || ""} onChange={(e) => setField("model", e.target.value)} />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Centros (vacío = todos)</Label>
                    <div className="flex flex-wrap gap-2">
                      {centers.map(c => (
                        <label key={c.slug} className="inline-flex items-center gap-1 text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1 cursor-pointer">
                          <input type="checkbox"
                            checked={(editForm.centers || []).includes(c.slug)}
                            onChange={(e) => {
                              const cur = editForm.centers || [];
                              setField("centers", e.target.checked
                                ? [...cur, c.slug]
                                : cur.filter(x => x !== c.slug));
                            }}
                          />
                          {c.name}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Observaciones internas</Label>
                    <Textarea rows={2} value={editForm.internal_notes || ""}
                      onChange={(e) => setField("internal_notes", e.target.value)} />
                  </div>
                </>
              )}

              {editing.type === "contact" && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Teléfono interno</Label>
                      <Input value={editForm.phone || ""} onChange={(e) => setField("phone", e.target.value)}
                        data-testid="editor-phone" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Email interno</Label>
                      <Input value={editForm.email || ""} onChange={(e) => setField("email", e.target.value)}
                        data-testid="editor-email" />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Centro (vacío = todos)</Label>
                    <Select value={editForm.center || "__all__"}
                      onValueChange={(v) => setField("center", v === "__all__" ? "" : v)}>
                      <SelectTrigger data-testid="editor-center"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">Todos</SelectItem>
                        {centers.map(c => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              <div className="space-y-1">
                <Label className="text-xs">Orden</Label>
                <Input type="number" value={editForm.order ?? 0}
                  onChange={(e) => setField("order", parseInt(e.target.value || 0, 10))} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={save} data-testid="editor-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) { setConfirmDelete(null); setDeleteConfirmText(""); } }}>
        <DialogContent className="max-w-md" data-testid="confirm-delete-dialog">
          <DialogHeader>
            <DialogTitle>Desactivar elemento</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Este elemento se desactivará y dejará de aparecer en nuevas citas, pero
            se conservará en citas antiguas. Escribe <strong>DESACTIVAR</strong> para confirmar.
          </p>
          <Input
            data-testid="confirm-delete-input"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder="DESACTIVAR"
            className="mt-2"
            autoFocus
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setConfirmDelete(null); setDeleteConfirmText(""); }}>Cancelar</Button>
            <Button
              onClick={doDelete}
              variant="destructive"
              data-testid="confirm-delete-btn"
              disabled={deleteConfirmText !== "DESACTIVAR"}
            >Desactivar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmReactivate} onOpenChange={(o) => !o && setConfirmReactivate(null)}>
        <DialogContent className="max-w-sm" data-testid="confirm-reactivate-dialog">
          <DialogHeader>
            <DialogTitle>Reactivar elemento</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Volverá a aparecer en los selectores de nuevas citas. ¿Confirmar?
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmReactivate(null)}>Cancelar</Button>
            <Button onClick={doReactivate} data-testid="confirm-reactivate-btn">Reactivar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
